import { Box, TextButton } from '@wix/design-system';
import React, { useContext } from 'react';

import { RicosContext } from './../../../../../ricos-context';
import type { LogBiFn } from './../../../../../_shared/bi-context-provider/bi-context-provider';

import {
  MEDIA_SETTINGS_DELETE_ITEMS_BUTTON,
  MEDIA_SETTINGS_DESELECT_ALL_ITEMS_BUTTON,
  MEDIA_SETTINGS_EDIT_ITEM_BUTTON,
  MEDIA_SETTINGS_SELECT_ALL_ITEMS_BUTTON,
} from '../../../../data-hooks';

type Item = { [key: string]: any; selected: boolean };

type MediaItemActionMenuProps = {
  items: Item[];
  onSelectAllClick: () => void;
  onUnselectAllClick: () => void;
  onDeleteClick: () => void;
  onEditClick: (isMediaSettings: boolean) => void;
  logBi: LogBiFn;
};

export const MediaItemActionMenu: React.FC<MediaItemActionMenuProps> = ({
  onSelectAllClick,
  onUnselectAllClick,
  onDeleteClick,
  items,
  onEditClick,
  logBi,
}) => {
  const { t } = useContext(RicosContext);

  const selectedItems = items.filter(item => item.selected);

  return (
    <Box maxHeight="fit-content" align="space-between" marginBottom="SP3">
      {selectedItems.length === 0 ? (
        <ItemActionButton
          key="selectAllButton"
          dataHook={MEDIA_SETTINGS_SELECT_ALL_ITEMS_BUTTON}
          onClick={onSelectAllClick}
          label={t('GallerySettings_Select_All') + ` (${items.length})`}
          logBi={logBi}
        />
      ) : (
        <ItemActionButton
          key="deselectAllButton"
          dataHook={MEDIA_SETTINGS_DESELECT_ALL_ITEMS_BUTTON}
          onClick={onUnselectAllClick}
          label={t('GallerySettings_Deselect') + ` (${selectedItems.length})`}
          logBi={logBi}
        />
      )}
      <Box gap="SP2">
        {selectedItems.length >= 1 && (
          <ItemActionButton
            key="deleteButton"
            dataHook={MEDIA_SETTINGS_DELETE_ITEMS_BUTTON}
            onClick={onDeleteClick}
            label={t('GallerySettings_Delete')}
            logBi={logBi}
          />
        )}
        {selectedItems.length === 1 && (
          <ItemActionButton
            key="itemSettingsButton"
            dataHook={MEDIA_SETTINGS_EDIT_ITEM_BUTTON}
            onClick={() => onEditClick(true)}
            label={t('GallerySettings_Image_Settings')}
            logBi={logBi}
          />
        )}
      </Box>
    </Box>
  );
};

const ItemActionButton: React.FC<{
  onClick: () => void;
  label: string;
  dataHook: string;
  logBi: LogBiFn;
}> = ({ label, onClick, dataHook, logBi }) => {
  return (
    <TextButton
      aria-label={label}
      role="menuitem"
      size="small"
      onClick={() => {
        logBi({ dataHook });
        onClick();
      }}
      dataHook={dataHook}
      underline="always"
    >
      {label}
    </TextButton>
  );
};
